// Renders a report result — either a grouped bar chart + aggregate table,
// or a flat record table if the report is ungrouped.
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Cell,
} from "recharts";

export interface ReportResult {
  groupBy?: string;
  aggregate: "count" | "sum";
  rows: Array<Record<string, any>>;
  total?: number;
}

const CHART_COLORS = ["#60a5fa", "#34d399", "#f87171", "#fbbf24", "#a78bfa", "#2dd4bf", "#fb7185", "#94a3b8"];

function currencyOrCount(mode: "count" | "sum", n: number) {
  if (mode === "count") return n.toLocaleString();
  return `$${Math.round(n).toLocaleString()}`;
}

export function ReportResultView({ result }: { result: ReportResult }) {
  const grouped = !!result.groupBy;

  if (!grouped) {
    // Flat table — show whatever columns exist.
    const columns = inferColumns(result.rows);
    return (
      <Card>
        <CardHeader><CardTitle className="text-base">Records ({result.rows.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          {result.rows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No matching records.</p>
          ) : (
            <div className="overflow-auto max-h-[480px]">
              <Table>
                <Thead>
                  <Tr>{columns.map((c) => <Th key={c}>{humanize(c)}</Th>)}</Tr>
                </Thead>
                <Tbody>
                  {result.rows.slice(0, 200).map((row, i) => (
                    <Tr key={i}>
                      {columns.map((c) => <Td key={c} className="text-sm">{renderCell(row[c])}</Td>)}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
              {result.rows.length > 200 && (
                <div className="border-t p-2 text-center text-xs text-muted-foreground">
                  Showing first 200 of {result.rows.length} rows.
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Grouped — chart + table
  const chartData = result.rows.map((r) => ({
    name: String(r.groupName ?? r.group ?? "—"),
    value: result.aggregate === "sum" ? r.sum : r.count,
  }));

  const totalLabel = result.aggregate === "sum" ? "Total value" : "Total records";

  return (
    <div className="grid gap-4 md:grid-cols-5">
      <Card className="md:col-span-3">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-base">By {humanize(result.groupBy!)}</CardTitle>
          <Badge variant="secondary">{totalLabel}: {currencyOrCount(result.aggregate, result.total ?? 0)}</Badge>
        </CardHeader>
        <CardContent>
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 40, top: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  type="number"
                  tickFormatter={(n) => currencyOrCount(result.aggregate, Number(n))}
                  tick={{ fontSize: 11 }}
                />
                <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v: any) => currencyOrCount(result.aggregate, Number(v))} />
                <Bar dataKey="value" radius={[4, 4, 4, 4]}>
                  {chartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card className="md:col-span-2">
        <CardHeader><CardTitle className="text-base">Breakdown</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <Thead>
              <Tr>
                <Th>{humanize(result.groupBy!)}</Th>
                <Th className="text-right">{result.aggregate === "sum" ? "Sum" : "Count"}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {result.rows.map((r, i) => (
                <Tr key={i}>
                  <Td>{String(r.groupName ?? r.group ?? "—")}</Td>
                  <Td className="text-right tabular-nums">
                    {currencyOrCount(result.aggregate, result.aggregate === "sum" ? r.sum : r.count)}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function humanize(s: string) {
  return s
    .replace(/([A-Z])/g, " $1")
    .replace(/[_.]/g, " ")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

function inferColumns(rows: any[]): string[] {
  if (!rows.length) return [];
  const preferred = ["name", "subject", "title", "company", "email", "stage", "status", "venue", "location", "scheduledAt", "valueUsd", "createdAt"];
  const present = new Set(Object.keys(rows[0]));
  return [...preferred.filter((c) => present.has(c)), ...[...present].filter((c) => !preferred.includes(c) && !c.endsWith("At") && !c.includes("body"))].slice(0, 8);
}

function renderCell(v: any): React.ReactNode {
  if (v === null || v === undefined) return "—";
  if (v instanceof Date) return v.toLocaleString();
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v)) return new Date(v).toLocaleString();
  if (typeof v === "object") return <span className="text-xs text-muted-foreground">(object)</span>;
  return String(v);
}
